import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Cloud, Database, MoreVertical } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { getTenantColor, getCloudProviderColor } from '../lib/colors';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { openMasLogin } from '../lib/utils';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { PageHeader } from '../components/ui/PageHeader';
import { LastSynced } from '../components/ui/LastSynced';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { DropdownMenu } from '../components/ui/DropdownMenu';

interface MASInstance {
  instanceName: string;
  tenantName: string;
  tenantCode: string;
  cloudProvider: string;
  clusterName: string;
  namespace: string;
  apiUrl?: string;
}

interface TenantInstances {
  tenant: string;
  tenantName: string;
  tenantColor?: string | null;
  providers: {
    aws: MASInstance[];
    oci: MASInstance[];
    hetzner: MASInstance[];
  };
}

export default function MASInstances() {
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('mas_instances', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const [tenantInstances, setTenantInstances] = useState<TenantInstances[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useActivityHeartbeat('cache:mas_instances', canView);

  const loadMASInstances = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

      if (tenantsToQuery.length === 0) {
        setTenantInstances([]);
        return;
      }

      // Fetch all tenants to get their display names and colors
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData: any[] = Array.isArray(tenantsResponse) ? tenantsResponse : (tenantsResponse as any).tenants || [];
      const tenantMap: Map<string, { name: string; color?: string | null }> = new Map(
        tenantsData.map((t: any) => [String(t.code || '').toLowerCase(), { name: t.name, color: t.color }])
      );

      const response = await apiClient.getMASInstances();
      setSyncedAt(response.syncedAt ?? null);
      const allInstances: MASInstance[] = response.instances || [];

      // Filter by selected tenants
      const wanted = new Set(tenantsToQuery.map(t => t.toLowerCase()));
      const filteredInstances = allInstances.filter(instance =>
        wanted.has(instance.tenantCode.toLowerCase())
      );

      // Group by tenant and cloud provider
      const groupedByTenant: Record<string, TenantInstances> = {};

      for (const instance of filteredInstances) {
        if (!groupedByTenant[instance.tenantCode]) {
          const tenantInfo = tenantMap.get(instance.tenantCode.toLowerCase());
          
          groupedByTenant[instance.tenantCode] = {
            tenant: instance.tenantCode,
            tenantName: tenantInfo?.name || instance.tenantName,
            tenantColor: tenantInfo?.color,
            providers: {
              aws: [],
              oci: [],
              hetzner: [],
            },
          };
        }

        const provider = instance.cloudProvider.toLowerCase() as 'aws' | 'oci' | 'hetzner';
        groupedByTenant[instance.tenantCode].providers[provider].push(instance);
      }

      setTenantInstances(Object.values(groupedByTenant));
    } catch (err: any) {
      console.error('Failed to load MAS instances:', err);
      setError(err.message || 'Failed to load MAS instances');
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenants, accessibleTenants]);

  useEffect(() => {
    void loadMASInstances();
  }, [loadMASInstances]);

  // Refresh the list when the backend cache updates (SSE), so instances appear/disappear without a page reload.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['mas_instances'],
    onEvent: () => {
      void loadMASInstances();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-96" />
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Only MAS instances on running OpenShift clusters are shown in this app
        </p>
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalInstances = tenantInstances.reduce((sum, tc) => 
    sum + tc.providers.oci.length + tc.providers.hetzner.length + tc.providers.aws.length, 0
  );

  const getProviderIcon = (provider: 'aws' | 'oci' | 'hetzner') => {
    return <Cloud className="w-4 h-4" style={{ color: getCloudProviderColor(provider) }} />;
  };

  const renderCardMenu = (instance: MASInstance) => (
    <DropdownMenu
      trigger={
        <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>
      }
      items={[
        {
          label: 'MAS Login',
          onClick: () => void openMasLogin({
            instanceName: instance.instanceName,
            clusterName: instance.clusterName,
            tenantCode: instance.tenantCode,
            cloudProvider: instance.cloudProvider,
          }),
        },
      ]}
      align="end"
    />
  );

  return (
    <div className="space-y-6">
      <div>
        <PageHeader
          icon={Database}
          iconColor="text-green-600 dark:text-green-400"
          iconBgColor="bg-green-100 dark:bg-green-900/30"
          description="Monitor MAS instances on running OpenShift clusters"
          count={totalInstances}
          countLabel="instances"
          countColor="text-green-600 dark:text-green-400"
        />
        <LastSynced iso={syncedAt} className="mt-2" />
      </div>

      {tenantInstances.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No MAS instances found for the selected tenants.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tenantInstances.map((tenantData) => {
            const tenantTotal = tenantData.providers.oci.length + 
                              tenantData.providers.hetzner.length + 
                              tenantData.providers.aws.length;
            
            if (tenantTotal === 0) return null;

            return (
              <div key={tenantData.tenant} className="space-y-6">
                {/* Tenant Header */}
                <div className="flex items-center justify-between">
                  <TenantHeading
                    tenant={resolveTenantMeta(tenantMetaMap, tenantData.tenant, {
                      name: tenantData.tenantName,
                      color: tenantData.tenantColor,
                    })}
                    title={tenantData.tenantName || tenantData.tenant}
                  />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {tenantTotal} instance{tenantTotal !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* AWS Instances */}
                {tenantData.providers.aws.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('aws')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('aws') }}>
                        AWS
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.aws.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-visible">
                      {tenantData.providers.aws.map((instance, idx) => (
                        <Card key={idx} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-visible">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                  <Database className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.instanceName}</CardTitle>
                                  <CardDescription className="mt-1 text-xs">
                                    <span className="block truncate">Cluster: {instance.clusterName}</span>
                                    <span className="block truncate">{instance.namespace}</span>
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {canView && renderCardMenu(instance)}
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* OCI Instances */}
                {tenantData.providers.oci.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('oci')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('oci') }}>
                        OCI
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.oci.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-visible">
                      {tenantData.providers.oci.map((instance, idx) => (
                        <Card key={idx} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-visible">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                  <Database className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.instanceName}</CardTitle>
                                  <CardDescription className="mt-1 text-xs">
                                    <span className="block truncate">Cluster: {instance.clusterName}</span>
                                    <span className="block truncate">{instance.namespace}</span>
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {canView && renderCardMenu(instance)}
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hetzner Instances */}
                {tenantData.providers.hetzner.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('hetzner')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('hetzner') }}>
                        Hetzner
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.hetzner.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-visible">
                      {tenantData.providers.hetzner.map((instance, idx) => (
                        <Card key={idx} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-visible">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                  <Database className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.instanceName}</CardTitle>
                                  <CardDescription className="mt-1 text-xs">
                                    <span className="block truncate">Cluster: {instance.clusterName}</span>
                                    <span className="block truncate">{instance.namespace}</span>
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {canView && renderCardMenu(instance)}
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
